"use client";

import * as React from "react";

import { APIProvider, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";

import CompactInput from "@/components/compact-input";
import Input from "@/components/input";
import type { InputProps } from "@/components/input";
import { parseAddressComponents } from "@/utils/address-components";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "@/components/ui/popover";
import { env, cn } from "@/utils";

// --- BasePlacesAutocomplete: your original implementation ---
export type PlaceAutocompleteProps = {
  onPlaceSelect: (place: any | null) => void;
  disabled?: boolean;
  compact?: boolean;
  wrapperClassName?: string;
  inputClassName?: string;
} & Pick<
  InputProps,
  "hint" | "state" | "onChange" | "value" | "label" | "placeholder"
>;

export function PlacesAutocomplete(props: PlaceAutocompleteProps) {
  return (
    <APIProvider apiKey={env.VITE_GOOGLE_MAPS_API_KEY}>
      <InnerPlacesAutocomplete {...props} />
    </APIProvider>
  );
}

// --- New PlacesAutocomplete: shadcn/ui + Google Places ---
export function InnerPlacesAutocomplete({
  onPlaceSelect,
  value = "",
  onChange,
  label,
  placeholder,
  state: inputState = "default",
  hint,
  disabled,
  wrapperClassName,
  inputClassName,
  compact = false,
}: PlaceAutocompleteProps) {
  const { t } = useTranslation("common");
  const resolvedPlaceholder = placeholder ?? t("places.defaultPlaceholder");
  const [open, setOpen] = React.useState(false);
  const [predictions, setPredictions] = React.useState<Array<any>>([]);
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);
  // useMapsLibrary returns undefined until the library is loaded
  const places = useMapsLibrary("places");
  const sessionTokenRef = React.useRef<any>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  // Session token for Google Places API
  React.useEffect(() => {
    if (!places) return;
    try {
      const SessionToken = (places as any).AutocompleteSessionToken;
      if (SessionToken) sessionTokenRef.current = new SessionToken();
    } catch {
      // ignore
    }
  }, [places]);

  // Fetch predictions when value changes
  React.useEffect(() => {
    // places is undefined until the library is loaded
    if (places == null) {
      return;
    }
    const inputValue = typeof value === "string" ? value : String(value);
    if (inputValue.trim().length < 1) {
      setPredictions([]);
      setActiveIndex(-1);
      return;
    }
    const t = setTimeout(() => {
      try {
        const AutocompleteService =
          window.google.maps.places.AutocompleteService;
        const service = new AutocompleteService();
        service.getPlacePredictions(
          {
            input: inputValue,
            sessionToken: sessionTokenRef.current,
          },
          (results: Array<any> | null, status: string) => {
            console.log(
              "getPlacePredictions results:",
              results,
              "status:",
              status,
            );
            setPredictions(results ?? []);
            setActiveIndex(-1);
          },
        );
      } catch (err) {
        console.log("Error in getPlacePredictions:", err);
        setPredictions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [value, places]);

  // Scroll active item into view
  function scrollActiveIntoView(idx: number) {
    if (!contentRef.current) return;
    const el = contentRef.current.querySelector(`[data-index="${idx}"]`);
    if (el) {
      (el as HTMLElement).scrollIntoView({ block: "nearest" });
    }
  }

  // Handle prediction selection
  function handleSelectPrediction(pred: any) {
    if (!pred) return;
    setOpen(false);
    try {
      const PlacesService = window.google.maps.places.PlacesService;
      const map = document.createElement("div");
      const service = new PlacesService(map);
      service.getDetails(
        {
          placeId: pred.place_id,
          fields: [
            "name",
            "formatted_address",
            "geometry",
            "address_components",
          ],
        },
        (place: any, status: string) => {
          if (status === "OK" && place) {
            const formattedAddress = place.formatted_address || "";
            const name = place.name || "";
            const location =
              place.geometry && place.geometry.location
                ? {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                  }
                : { lat: 0, lng: 0 };
            onPlaceSelect({
              formattedAddress,
              name,
              location,
              components: parseAddressComponents(place.address_components),
            });
          } else {
            onPlaceSelect(null);
          }
        },
      );
    } catch {
      onPlaceSelect(null);
    }
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (predictions.length === 0) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setOpen(true);
        setActiveIndex(-1);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => {
        const next = prev < 0 ? 0 : Math.min(prev + 1, predictions.length - 1);
        scrollActiveIntoView(next);
        return next;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => {
        const next = prev <= 0 ? -1 : prev - 1;
        if (next === -1) scrollActiveIntoView(0);
        else scrollActiveIntoView(next);
        return next;
      });
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0) {
        e.preventDefault();
        const p = predictions[activeIndex];
        if (p) handleSelectPrediction(p);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div className={cn(wrapperClassName)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          {compact ? (
            <CompactInput
              value={value}
              placeholder={resolvedPlaceholder}
              onChange={(e) => {
                onChange?.(e);
                setOpen(true);
              }}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                setTimeout(() => {
                  if (
                    contentRef.current &&
                    contentRef.current.contains(document.activeElement)
                  ) {
                    return;
                  }
                  setOpen(false);
                }, 0);
              }}
              onKeyDown={handleInputKeyDown}
              className={cn("!h-full pr-10", inputClassName)}
            />
          ) : (
            <Input
              value={value}
              placeholder={resolvedPlaceholder}
              onChange={(e) => {
                onChange?.(e);
                setOpen(true);
              }}
              disabled={disabled}
              label={label}
              hint={hint}
              state={inputState}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                setTimeout(() => {
                  if (
                    contentRef.current &&
                    contentRef.current.contains(document.activeElement)
                  ) {
                    return;
                  }
                  setOpen(false);
                }, 0);
              }}
              onKeyDown={handleInputKeyDown}
              readOnly={false}
              className={cn("pr-10", inputClassName)}
            />
          )}
        </PopoverAnchor>
        <PopoverContent
          ref={contentRef}
          className="p-0 w-[260px] bg-popover text-garnet rounded-md border shadow-md"
          role="listbox"
          aria-label={t("places.suggestionsLabel")}
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="flex flex-col">
            <div className="max-h-60 overflow-auto">
              {predictions.map((p, i) => {
                const desc =
                  p.description ||
                  (p.structured_formatting &&
                    p.structured_formatting.main_text) ||
                  "";
                const isActive = i === activeIndex;
                return (
                  <button
                    key={p.place_id ?? i}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectPrediction(p);
                    }}
                    onMouseEnter={() => setActiveIndex(i)}
                    data-index={i}
                    role="option"
                    aria-selected={isActive}
                    className={cn(
                      "flex items-center gap-2 px-4 py-3 text-sm hover:bg-muted w-full text-left text-white",
                      isActive && "bg-muted",
                    )}
                  >
                    <span className="truncate">{desc}</span>
                  </button>
                );
              })}
              {predictions.length === 0 && (
                <div className="px-4 py-3 text-sm text-muted-foreground">
                  {t("status.noResults")}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
